import React, { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import date from 'date-and-time';
import { SmartLinkWidgetType } from 'fym-common/src/db/enums';
import { SerializedPresaveWidget } from 'fym-common/src/db/serializersTypes';
import { defaultPresaveServices } from 'fym-shared/src/smartlinks/presave';
import { TypedMemo } from '../../../../../common/types';
import { WidgetEditionProps } from '../../props';
import { FieldsType } from '../../../../wizards/presaveLink/consts';
import { usePresaveLinkForm } from '../../../../wizards/presaveLink/usePresaveLinkForm';
import { PresaveServicesField } from '../../../../wizards/presaveLink/PresaveServicesField';
import { useUpdatePresaveCampaignMutation } from '../../../../../../redux/api/fymAPI';
import { PresaveCampaign } from '../../../../../../redux/api/fymAPI.types';
import { isSuccessfulResult } from '../../../../../../redux/api/utils/isSuccessfulResult';
import { PresaveWidgetFormContainer } from './PresaveWidgetFormContainer';

interface PresaveWidgetEditFormProps
  extends Pick<
    WidgetEditionProps<SerializedPresaveWidget>,
    'accountId' | 'smartLinkId' | 'saveWidget' | 'deleteWidget' | 'isLoading' | 'error'
  > {
  widget: SerializedPresaveWidget;
  campaign: PresaveCampaign;
}

/**
 * Editing a campaign that has not landed yet.
 *
 * Two homes for the fields, so two calls: the title, the artist and the date
 * belong to the campaign, and the list of services belongs to the block. Both
 * are written on one submit, campaign first — the widget call is the one that
 * navigates back.
 */
const PresaveWidgetEditFormComponent: React.FC<PresaveWidgetEditFormProps> = ({
  accountId,
  smartLinkId,
  saveWidget,
  deleteWidget,
  isLoading,
  error,
  widget,
  campaign,
}) => {
  const intl = useIntl();
  const [services, setServices] = useState<string[]>(widget.data.services ?? defaultPresaveServices);
  const [updateCampaign, { isLoading: isUpdatingCampaign, error: updateCampaignError }] =
    useUpdatePresaveCampaignMutation();

  const prefill = useMemo(
    () => ({
      title: campaign.trackTitle,
      artistName: campaign.artistName,
      // A day, stored at midnight UTC. Read back in UTC so the field does not
      // show the day before for an artist west of Greenwich.
      releaseDate: date.format(new Date(campaign.releaseDate), 'YYYY-MM-DD', true),
    }),
    [campaign.artistName, campaign.releaseDate, campaign.trackTitle]
  );

  const onSuccess = useCallback(
    (values: FieldsType) => {
      const run = async (): Promise<void> => {
        const result = await updateCampaign({
          accountId,
          campaignId: campaign.id,
          campaignUuid: campaign.uuid,
          smartLinkId,
          title: values.title,
          artistName: values.artistName,
          releaseDate: values.releaseDate,
        });
        // The campaign released or was called off while this screen was open.
        // The server says so; the block is left alone.
        if (!isSuccessfulResult(result)) {
          return;
        }
        // The catalog song of the block stays bound: after the release it shows the song's links.
        const catalogItemUuid = widget.data.catalogItemUuid ?? undefined;
        await saveWidget(
          catalogItemUuid === undefined
            ? { campaignUuid: campaign.uuid, services }
            : { campaignUuid: campaign.uuid, catalogItemUuid, services },
          SmartLinkWidgetType.presave
        );
      };
      run().catch(console.error);
    },
    [
      accountId,
      campaign.id,
      campaign.uuid,
      saveWidget,
      services,
      smartLinkId,
      updateCampaign,
      widget.data.catalogItemUuid,
    ]
  );

  const busy = isLoading || isUpdatingCampaign;
  const { submit, TitleInput, ArtistNameInput, ReleaseDateInput } = usePresaveLinkForm({
    prefill,
    isLoading: busy,
    onSuccess,
  });

  return (
    <PresaveWidgetFormContainer
      accountId={accountId}
      smartLinkId={smartLinkId}
      isLoading={busy}
      error={error ?? updateCampaignError}
      onSubmit={submit}
      onDelete={deleteWidget}
      submitDisabled={services.length === 0}
    >
      {TitleInput}
      {ArtistNameInput}
      {ReleaseDateInput}
      <PresaveServicesField
        selected={services}
        onChange={setServices}
        disabled={busy}
        label={intl.formatMessage({ id: 'app.sml.presave.form.services', defaultMessage: 'Pre-save on' })}
        help={intl.formatMessage({
          id: 'app.sml.presave.form.services_help',
          defaultMessage: 'Fans on these services save the track the moment you release it. Pick at least one.',
        })}
      />
    </PresaveWidgetFormContainer>
  );
};

export const PresaveWidgetEditForm = TypedMemo(PresaveWidgetEditFormComponent);
